import { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../config';
import { standingsApi, Standing } from '../utils/standingsApi';

interface StandingsByLeague {
  [league: string]: Standing[];
}

export const useStandings = () => {
  const [standings, setStandings] = useState<StandingsByLeague>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStandings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await standingsApi.getStandings();
      if (response.success && response.standings) {
        console.log('📊 Standings fetched:', response.standings);
        // Create a new object reference to ensure React detects the change
        const newStandings = { ...response.standings };
        // Also create new arrays for each league to ensure deep equality
        Object.keys(newStandings).forEach(league => {
          newStandings[league] = [...newStandings[league]];
        });
        console.log('📊 Setting new standings object:', newStandings);
        setStandings(newStandings);
      } else {
        console.warn('⚠️ Standings response missing data:', response);
      }
    } catch (err) {
      console.error('Failed to fetch standings:', err);
      setError('Failed to fetch standings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get standing for a specific team
  const getTeamStanding = useCallback((teamId: string): Standing | null => {
    for (const league of Object.keys(standings)) {
      const standing = standings[league].find(s => s.team_id === teamId);
      if (standing) {
        return standing;
      }
    }
    return null;
  }, [standings]);

  // Initial fetch
  useEffect(() => {
    fetchStandings();
  }, [fetchStandings]);

  // Set up Socket.IO connection for real-time updates
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Connected to standings server');
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from standings server');
    });

    // Listen for game completion events to refresh standings
    newSocket.on('gameCompleted', (data) => {
      console.log('🎮 gameCompleted event received:', data);
      console.log('🔄 Refreshing standings in 500ms (to ensure DB write committed)...');
      // Small delay to ensure database write has fully committed
      setTimeout(() => {
        console.log('🔄 Now fetching standings...');
        fetchStandings();
      }, 500);
    });

    newSocket.on('continuous-game-complete', (data) => {
      console.log('🎮 continuous-game-complete event received:', data);
      console.log('🔄 Refreshing standings in 500ms (to ensure DB write committed)...');
      // Small delay to ensure database write has fully committed
      setTimeout(() => {
        console.log('🔄 Now fetching standings...');
        fetchStandings();
      }, 500);
    });

    newSocket.on('weekCompleted', (data) => {
      console.log('📅 weekCompleted event received:', data);
      console.log('🔄 Refreshing standings...');
      fetchStandings();
    });

    newSocket.on('scheduledGameCompleted', (data) => {
      console.log('📅 scheduledGameCompleted event received:', data);
      console.log('🔄 Refreshing standings...');
      fetchStandings();
    });

    return () => {
      newSocket.close();
    };
  }, [fetchStandings]);

  return {
    standings,
    isLoading,
    error,
    fetchStandings,
    getTeamStanding
  };
};

